import { PartialRealisationAllocationLogic } from 'further-types/lp/realisation';
import { useEffect, useMemo } from 'react';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { sortBy } from 'lodash';
import getUnitsAllocation from '../helpers/getUnitsAllocation';
import { realisation as rules } from 'further-ui/validations';
import { InvestmentHistory } from '../../types';

type UseUnitAllocationProps = {
  index: number;
  unitsSold: number;
  selectedPartialRealisationAllocationLogic: PartialRealisationAllocationLogic;
  holdingClassTotal: { currentNumberOfUnits: number };
  investmentHistory: InvestmentHistory;
  setValue: UseFormSetValue<rules.CreateRealisationFormValues>;
  getValues: UseFormGetValues<rules.CreateRealisationFormValues>;
  selectedHoldingClassId?: string;
};

const useUnitAllocation = ({
  index,
  unitsSold,
  selectedPartialRealisationAllocationLogic,
  holdingClassTotal,
  investmentHistory,
  setValue,
  getValues,
  selectedHoldingClassId,
}: UseUnitAllocationProps) => {
  const holdingClassInvestmentHistory = useMemo(
    () =>
      investmentHistory.filter(
        (history) => history.holdingClassId === selectedHoldingClassId,
      ),
    [investmentHistory, selectedHoldingClassId],
  );

  useEffect(() => {
    if (
      selectedPartialRealisationAllocationLogic !==
      PartialRealisationAllocationLogic.Manual
    ) {
      const newAllocations = getUnitsAllocation(
        holdingClassInvestmentHistory,
        unitsSold,
        holdingClassTotal.currentNumberOfUnits,
        selectedPartialRealisationAllocationLogic,
      );

      const holdingClassName =
        holdingClassInvestmentHistory.find(
          (history) => history.holdingClassId === selectedHoldingClassId,
        )?.name ?? '';

      setValue(`holdingClassRealisation.${index}.name`, holdingClassName);

      const sortedAllocations = sortBy(
        newAllocations.map((unit) => ({
          ...unit,
          holdingClassId: selectedHoldingClassId,
        })),
        'date',
      );

      setValue(
        `holdingClassRealisation.${index}.unitsAllocationPerInvestment`,
        sortedAllocations,
        { shouldDirty: true },
      );
    }
  }, [
    unitsSold,
    selectedPartialRealisationAllocationLogic,
    holdingClassTotal.currentNumberOfUnits,
    index,
    selectedHoldingClassId,
  ]);

  const onManualAllocationChange = () => {
    setValue(
      `holdingClassRealisation.${index}.partialRealisationAllocationLogic`,
      PartialRealisationAllocationLogic.Manual,
    );
  };

  // Always get the current values from the form
  const unitsAllocationPerInvestment =
    getValues(
      `holdingClassRealisation.${index}.unitsAllocationPerInvestment`,
    ) ?? [];

  return {
    onManualAllocationChange,
    unitsAllocationPerInvestment,
  };
};

export default useUnitAllocation;
